import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faUsers, faGlobe } from '@fortawesome/free-solid-svg-icons';

const PlatformAdminGroupPicker = ({ groups, onSelect, onClose }) => {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'manual' | 'scraped'
  const inputRef = useRef(null);

  useEffect(() => {
    // Focus search on mount
    setTimeout(() => inputRef.current?.focus(), 50);
    // Prevent background scrolling
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const filtered = useMemo(() => {
    let list = groups;
    if (filter !== 'all') list = list.filter(g => g.source_type === filter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(g =>
        g.name?.toLowerCase().includes(q) ||
        g.city?.toLowerCase().includes(q) ||
        g.state?.toLowerCase().includes(q) ||
        g.slug?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [groups, query, filter]);

  const manualCount  = groups.filter(g => g.source_type === 'manual'  || !g.source_type).length;
  const scrapedCount = groups.filter(g => g.source_type === 'scraped').length;

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px'
      }}
    >
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 620,
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,0.25)', overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 22 }}>👑</span>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111' }}>
              Platform Admin — Select a Community
            </h2>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#666' }}>
            {groups.length} communities · {manualCount} claimed · {scrapedCount} scraped
          </p>

          {/* Search */}
          <div style={{ position: 'relative', marginTop: 14 }}>
            <FontAwesomeIcon icon={faSearch} style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              color: '#aaa', fontSize: 13
            }} />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by name, city, state…"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '9px 36px 9px 34px', border: '1.5px solid #e0e0e0',
                borderRadius: 8, fontSize: 14, outline: 'none',
                fontFamily: 'inherit'
              }}
              onFocus={e => e.target.style.borderColor = '#6366f1'}
              onBlur={e => e.target.style.borderColor = '#e0e0e0'}
            />
            {query && (
              <button onClick={() => setQuery('')} style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', padding: 2
              }}>
                <FontAwesomeIcon icon={faTimes} fontSize={12} />
              </button>
            )}
          </div>

          {/* Filter pills */}
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            {[
              { key: 'all',     label: `All (${groups.length})` },
              { key: 'manual',  label: `Claimed (${manualCount})` },
              { key: 'scraped', label: `Scraped (${scrapedCount})` },
            ].map(({ key, label }) => (
              <button key={key} onClick={() => setFilter(key)} style={{
                padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: '1.5px solid',
                borderColor: filter === key ? '#6366f1' : '#e0e0e0',
                background: filter === key ? '#6366f1' : '#fff',
                color: filter === key ? '#fff' : '#555',
                cursor: 'pointer', transition: 'all 0.15s'
              }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#999', fontSize: 14 }}>
              No communities match "{query}"
            </div>
          ) : (
            filtered.map(group => (
              <button
                key={group.id}
                onClick={() => onSelect(group)}
                style={{
                  width: '100%', textAlign: 'left', background: 'none',
                  border: 'none', borderBottom: '1px solid #f5f5f5',
                  padding: '12px 20px', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', gap: 12, transition: 'background 0.1s',
                  fontFamily: 'inherit'
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f9f9ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                {/* Avatar */}
                <div style={{
                  width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                  background: '#f0f0f0', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {group.logo_url
                    ? <img src={group.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <FontAwesomeIcon icon={faUsers} style={{ color: '#bbb', fontSize: 16 }} />
                  }
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {group.name}
                  </div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 1 }}>
                    {[group.city, group.state].filter(Boolean).join(', ') || group.slug || '—'}
                  </div>
                </div>

                {/* Badge */}
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                  background: group.source_type === 'scraped' ? '#fff7ed' : '#f0fdf4',
                  color:      group.source_type === 'scraped' ? '#c2410c' : '#16a34a',
                  flexShrink: 0
                }}>
                  {group.source_type === 'scraped' ? 'scraped' : 'claimed'}
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PlatformAdminGroupPicker;
